import React from "react";
import { MdEmail, MdAccessTime } from "react-icons/md";

const PrivacyPolicy = () => {
  return (
    <div className="max-w-[1280px] mx-auto md:p-6 p-4 leading-8 mt-5 md:mt-10 font-poppins">
      {/* Hero Section */}
      <div className="relative p-8 mb-8 overflow-hidden text-center border border-[#0B5647]/10 bg-gradient-to-br from-[#0B5647]/5 via-white to-slate-100 rounded-3xl shadow-sm">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#00261E] via-[#0B5647] to-[#00261E]"></div>
        <div className="relative z-10">
          <h1 className="text-3xl md:text-5xl font-black text-primary">
            Privacy Policy
          </h1>
        </div>
      </div>

      {/* Introduction */}
      <div className="relative p-6 md:p-8 mb-8 overflow-hidden border border-slate-200/60 shadow-sm bg-white rounded-2xl">
        <div className="absolute top-0 left-0 w-full h-1 bg-[#0c9e82]"></div>
        <div className="flex items-start">
          <div className="flex-1">
            <h3 className="mb-3 text-xl md:text-2xl font-black text-primary">
              Your Privacy Matters to Us
            </h3>
            <p className="text-sm md:text-base leading-relaxed text-slate-600">
              Airport Parking Deals ("we," "our," "us") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, store, and protect your data when you use our website or services.
            </p>
            <div className="flex items-center mt-4 space-x-2">
              <div className="w-2.5 h-2.5 bg-[#0c9e82] rounded-full animate-pulse"></div>
              <span className="text-xs md:text-sm font-semibold text-[#0c9e82]">Secure & GDPR Compliant</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section 1 Header */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <div className="inline-flex items-center space-x-3">
          <span className="flex items-center justify-center w-7 h-7 text-xs font-bold text-white rounded-full bg-[#0c9e82]">1</span>
          <h2 className="text-lg md:text-xl font-bold text-primary">
            Information We Collect
          </h2>
        </div>
      </div>

      <p className="mb-5 text-sm md:text-base text-slate-600 leading-relaxed">
        When you use our website, we may collect the following information:
      </p>

      {/* Grid of Cards */}
      <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-2">
        <div className="p-5 transition-shadow border border-[#0c9e82]/15 bg-[#0c9e82]/5 rounded-2xl hover:shadow-md">
          <div className="flex items-center mb-3 space-x-3">
            <div className="flex items-center justify-center w-9 h-9 bg-[#0c9e82] rounded-xl text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h4 className="font-bold text-primary text-sm md:text-base">Personal Information</h4>
          </div>
          <p className="text-xs md:text-sm text-slate-600 leading-relaxed">Name, email address, phone number, billing details, booking information.</p>
        </div>

        <div className="p-5 transition-shadow border border-[#0B5647]/15 bg-[#0B5647]/5 rounded-2xl hover:shadow-md">
          <div className="flex items-center mb-3 space-x-3">
            <div className="flex items-center justify-center w-9 h-9 bg-[#0B5647] rounded-xl text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 112 0v1m-6 0h12l-1 12H9L8 7z" />
              </svg>
            </div>
            <h4 className="font-bold text-primary text-sm md:text-base">Booking Details</h4>
          </div>
          <p className="text-xs md:text-sm text-slate-600 leading-relaxed">Travel dates, airport, selected parking service, vehicle details.</p>
        </div>

        <div className="p-5 transition-shadow border border-brandLime/20 bg-brandLime/5 rounded-2xl hover:shadow-md">
          <div className="flex items-center mb-3 space-x-3">
            <div className="flex items-center justify-center w-9 h-9 bg-[#00261E] rounded-xl text-brandLime">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <h4 className="font-bold text-primary text-sm md:text-base">Payment Information</h4>
          </div>
          <p className="text-xs md:text-sm text-slate-600 leading-relaxed">Processed securely through third-party payment processors; we do not store your full card details.</p>
        </div>

        <div className="p-5 transition-shadow border border-slate-200 bg-slate-100/50 rounded-2xl hover:shadow-md">
          <div className="flex items-center mb-3 space-x-3">
            <div className="flex items-center justify-center w-9 h-9 bg-slate-500 rounded-xl text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="font-bold text-primary text-sm md:text-base">Technical Information</h4>
          </div>
          <p className="text-xs md:text-sm text-slate-600 leading-relaxed">IP address, browser type, device information, and cookies for website functionality and analytics.</p>
        </div>
      </div>

      {/* Section 2 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          2. How We Use Your Information
        </h2>
      </div>

      <p className="mb-4 text-sm md:text-base text-slate-600">
        We use the information collected for the following purposes:
      </p>
      <ul className="pl-4 mb-8 space-y-3 list-none">
        {[
          "To process and confirm your bookings.",
          "To provide you with booking confirmation and relevant information.",
          "To communicate with you regarding amendments, cancellations, or customer support.",
          "To improve our website, services, and user experience.",
          "To comply with legal and regulatory requirements."
        ].map((item, index) => (
          <li key={index} className="flex items-start gap-2.5 text-slate-600 text-xs md:text-sm leading-relaxed">
            <span className="text-[#0c9e82] font-extrabold mt-0.5 shrink-0 select-none">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>

      {/* Section 3 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          3. Sharing Your Information
        </h2>
      </div>

      <p className="mb-4 text-sm md:text-base text-slate-600">
        We do not sell your personal data. However, we may share your information in the following situations:
      </p>
      <ul className="pl-4 mb-8 space-y-3 list-none">
        {[
          "With Parking Providers – To complete your booking and provide the service you selected.",
          "With Payment Processors – To securely process your payment.",
          "With Legal Authorities – If required by law, regulation, or legal process."
        ].map((item, index) => {
          const parts = item.split(" – ");
          return (
            <li key={index} className="flex items-start gap-2.5 text-slate-600 text-xs md:text-sm leading-relaxed">
              <span className="text-[#0c9e82] font-extrabold mt-0.5 shrink-0 select-none">•</span>
              <span>
                <strong>{parts[0]}</strong> – {parts[1]}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Section 4 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          4. Cookies & Tracking
        </h2>
      </div>

      <p className="mb-8 text-xs md:text-sm text-slate-600 leading-relaxed">
        Our website uses cookies to improve functionality, enhance security, and analyze website performance. You can manage or disable cookies in your browser settings, but some parts of the website may not function properly without them.
      </p>

      {/* Section 5 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          5. Data Security
        </h2>
      </div>

      <p className="mb-8 text-xs md:text-sm text-slate-600 leading-relaxed">
        We take appropriate technical and organizational measures to safeguard your personal information against unauthorized access, loss, or misuse.
      </p>

      {/* Section 6 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          6. Data Retention
        </h2>
      </div>

      <p className="mb-8 text-xs md:text-sm text-slate-600 leading-relaxed">
        We retain your information only for as long as necessary to fulfill booking requirements, comply with legal obligations, and resolve any disputes.
      </p>

      {/* Section 7 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          7. Your Rights
        </h2>
      </div>

      <p className="mb-4 text-sm md:text-base text-slate-600">
        You have the right to:
      </p>
      <ul className="pl-4 mb-8 space-y-3 list-none">
        {[
          "Access the personal information we hold about you.",
          "Request corrections to any inaccurate data.",
          "Request deletion of your personal data (subject to legal or contractual obligations).",
          "Withdraw consent for marketing communications at any time."
        ].map((item, index) => (
          <li key={index} className="flex items-start gap-2.5 text-slate-600 text-xs md:text-sm leading-relaxed">
            <span className="text-[#0c9e82] font-extrabold mt-0.5 shrink-0 select-none">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p className="mb-8 text-xs md:text-sm text-slate-600 leading-relaxed">
        To exercise your rights, please contact us at{" "}
        <a href="mailto:info@dummy.co.uk" className="text-[#0c9e82] hover:underline font-semibold">
          info@dummy.co.uk
        </a>
        .
      </p>

      {/* Section 8 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          8. Third-Party Links
        </h2>
      </div>

      <p className="mb-8 text-xs md:text-sm text-slate-600 leading-relaxed">
        Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those websites.
      </p>

      {/* Section 9 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          9. Children's Privacy
        </h2>
      </div>

      <p className="mb-8 text-xs md:text-sm text-slate-600 leading-relaxed">
        Our services are not directed at children under the age of 16. We do not knowingly collect personal data from children.
      </p>

      {/* Section 10 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          10. Updates to This Policy
        </h2>
      </div>

      <p className="mb-8 text-xs md:text-sm text-slate-600 leading-relaxed">
        We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.
      </p>

      {/* Section 11 */}
      <div className="relative pl-6 py-2 my-6 border-l-4 border-[#0c9e82] select-none">
        <h2 className="text-lg md:text-xl font-bold text-primary">
          11. Contact Us
        </h2>
      </div>

      <p className="mb-5 text-sm md:text-base text-slate-600 leading-relaxed">
        If you have any questions or concerns regarding this Privacy Policy, please contact us:
      </p>

      {/* Bottom Contact Container */}
      <div className="relative p-6 md:p-8 overflow-hidden border border-[#0B5647]/10 bg-gradient-to-br from-[#0B5647]/5 via-white to-[#00261E]/5 rounded-3xl shadow-sm">
        {/* Floating circles decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 -mt-16 -mr-16 rounded-full bg-gradient-to-br from-[#0c9e82]/10 to-brandLime/10 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 -mb-12 -ml-12 rounded-full bg-gradient-to-tr from-brandLime/10 to-[#0B5647]/10 pointer-events-none"></div>

        <div className="relative z-10">
          <div className="flex items-center mb-6 space-x-4">
            <div className="flex items-center justify-center shadow-md w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-[#00261E] to-[#0B5647] text-white">
              <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-black text-primary">
                Get in Touch
              </h3>
              <p className="text-xs md:text-sm font-semibold text-slate-500">We're here to help with any questions you may have</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="p-4 bg-white border border-slate-200/60 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3.5">
                <div className="flex items-center justify-center w-10 h-10 bg-[#0c9e82] text-white rounded-full">
                  <MdEmail className="text-lg" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray uppercase tracking-wider">Email Support</p>
                  <a
                    href="mailto:info@dummy.co.uk"
                    className="text-xs md:text-sm font-semibold text-[#0c9e82] hover:underline"
                  >
                    info@dummy.co.uk
                  </a>
                </div>
              </div>
            </div>

            <div className="p-4 bg-white border border-slate-200/60 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3.5">
                <div className="flex items-center justify-center w-10 h-10 bg-[#0B5647] text-white rounded-full">
                  <MdAccessTime className="text-lg" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray uppercase tracking-wider">Service Hours</p>
                  <p className="text-xs md:text-sm font-semibold text-primary">9:00 AM – 7:00 PM</p>
                  <p className="text-[10px] md:text-xs text-slate-400 font-medium">Monday to Friday</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 mt-6 border border-[#0B5647]/10 bg-[#0B5647]/5 rounded-2xl">
            <div className="flex items-center mb-2 space-x-2 select-none">
              <div className="w-2 h-2 bg-[#0c9e82] rounded-full animate-pulse"></div>
              <span className="text-xs md:text-sm font-bold text-[#0B5647] uppercase tracking-wide">Quick Response</span>
            </div>
            <p className="text-[11px] md:text-xs text-slate-500 leading-relaxed">
              We typically respond to privacy-related inquiries within 24-48 hours during business days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
